/**
 * Logger utility for conditional logging based on environment
 * In development: logs everything with filename and line number
 * In production: logs nothing (unless explicitly enabled)
 * 
 * Usage:
 * import { log, warn, error, info, debug } from '../utils/logger'
 * 
 * // These will only show in development with file location
 * log('This is a log message')
 * warn('This is a warning')
 * info('This is info')
 * debug('This is debug info')
 * 
 * // This will always show (even in production) with file location
 * error('This is an error')
 */

const isDevelopment = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) || process.env.NODE_ENV === 'development';
const isProduction = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD) || process.env.NODE_ENV === 'production';

interface Logger {
    log: (...args: unknown[]) => void
    warn: (...args: unknown[]) => void
    error: (...args: unknown[]) => void
    info: (...args: unknown[]) => void
    debug: (...args: unknown[]) => void
}

// Helper function to get caller location
const getCallerLocation = (): string => {
    try {
        const stack = new Error().stack
        if (!stack) return 'unknown'

        // Split stack into lines and find the caller (skip Error constructor and logger functions)
        const lines = stack.split('\n')
        const callerLine = lines.find(line =>
            line.includes('at ') &&
            !line.includes('logger.ts') &&
            !line.includes('Error') &&
            !line.includes('getCallerLocation')
        )

        if (callerLine) {
            // Extract file path and line number
            const match = callerLine.match(/at .+ \((.+):(\d+):(\d+)\)/)
            if (match) {
                const [, filePath, line] = match
                const fileName = filePath.split('/').pop() || 'unknown'
                // Remove query parameters from filename
                const cleanFileName = fileName.split('?')[0]
                return `${cleanFileName}:${line}`
            }

            // Fallback for different stack trace formats
            const fallbackMatch = callerLine.match(/at .+ (.+):(\d+):(\d+)/)
            if (fallbackMatch) {
                const [, filePath, line] = fallbackMatch
                const fileName = filePath.split('/').pop() || 'unknown'
                // Remove query parameters from filename
                const cleanFileName = fileName.split('?')[0]
                return `${cleanFileName}:${line}`
            }
        }

        return 'unknown'
    } catch {
        return 'unknown'
    }
}

// Helper function to format log message with location
const formatMessage = (level: string, args: unknown[]): unknown[] => {
    const location = getCallerLocation()
    return [`[${level}] ${location}:`, ...args]
}

// Create a logger that only logs in development
const createLogger = (): Logger => ({
    log: (...args: unknown[]) => {
        if (isDevelopment) {
            console.log(...formatMessage('LOG', args))
        }
    },
    warn: (...args: unknown[]) => {
        if (isDevelopment) {
            console.warn(...formatMessage('WARN', args))
        }
    },
    error: (...args: unknown[]) => {
        // Always log errors, even in production
        console.error(...formatMessage('ERROR', args))
    },
    info: (...args: unknown[]) => {
        if (isDevelopment) {
            console.info(...formatMessage('INFO', args))
        }
    },
    debug: (...args: unknown[]) => {
        if (isDevelopment) {
            console.debug(...formatMessage('DEBUG', args))
        }
    },
})

// Export the logger instance
export const logger = createLogger()

// Export individual methods for convenience
export const { log, warn, error, info, debug } = logger

// Export environment helpers
export { isDevelopment, isProduction } 